import { isEmpty } from "lodash"
import { createSelector } from "reselect"
import { NORMALIZATION, computeStandaloneSegment, filterSupportsContribution } from "helpers/segments"

export const FILTER_SCOPE = {
  PORTFOLIO_AND_BENCHMARK: "PORTFOLIO_AND_BENCHMARK",
  PORTFOLIO_ONLY: "PORTFOLIO_ONLY",
} as const

export type FilterScope = (typeof FILTER_SCOPE)[keyof typeof FILTER_SCOPE]

export const DEFAULT_FILTER_SCOPE: FilterScope = FILTER_SCOPE.PORTFOLIO_AND_BENCHMARK

export const FILTER_SCOPE_LABELS: Record<FilterScope, string> = {
  [FILTER_SCOPE.PORTFOLIO_AND_BENCHMARK]: "Filtered benchmark",
  [FILTER_SCOPE.PORTFOLIO_ONLY]: "Full benchmark",
}

interface EffectiveFilterScopeContext {
  hasBenchmark: boolean
  hasSimulatedTrades: boolean
  hasTagOrAttributeFilter: boolean
}

interface BaseChangeState {
  filter: unknown
  filterScope: unknown
  normalization: unknown
}

export function parseFilterScope(filterScope: unknown): FilterScope {
  return filterScope === FILTER_SCOPE.PORTFOLIO_ONLY ? FILTER_SCOPE.PORTFOLIO_ONLY : DEFAULT_FILTER_SCOPE
}

export function computeHasTagOrAttributeFilter(filter: unknown): boolean {
  return !filterSupportsContribution(filter, true)
}

export function computeEffectiveFilterScope(filterScope: unknown, { hasBenchmark, hasSimulatedTrades, hasTagOrAttributeFilter }: EffectiveFilterScopeContext): FilterScope {
  if (!hasBenchmark || hasSimulatedTrades) {
    return DEFAULT_FILTER_SCOPE
  }
  if (hasTagOrAttributeFilter) {
    return FILTER_SCOPE.PORTFOLIO_ONLY
  }
  return parseFilterScope(filterScope)
}

export function computeScopedSegment<Segment extends object | null | undefined>(segment: Segment, effectiveFilterScope: FilterScope): Segment {
  if (effectiveFilterScope !== FILTER_SCOPE.PORTFOLIO_ONLY) { return segment }
  return computeStandaloneSegment(segment)
}

export function computeScopedBaseSegment<Segment extends object | null | undefined>(segment: Segment, effectiveFilterScope: FilterScope): Segment | null {
  if (effectiveFilterScope === FILTER_SCOPE.PORTFOLIO_ONLY) { return null }
  return segment
}

export function computeEffectiveNormalization<Normalization>(normalization: Normalization, effectiveFilterScope: FilterScope): Normalization | typeof NORMALIZATION.STANDALONE {
  return effectiveFilterScope === FILTER_SCOPE.PORTFOLIO_ONLY ? NORMALIZATION.STANDALONE : normalization
}

export function computeBaseChangeNormalization({ filter, filterScope, normalization }: BaseChangeState, hasBenchmark: boolean): unknown {
  const effectiveFilterScope = computeEffectiveFilterScope(filterScope, {
    hasBenchmark,
    hasSimulatedTrades: false,
    hasTagOrAttributeFilter: computeHasTagOrAttributeFilter(filter),
  })
  return computeEffectiveNormalization(normalization, effectiveFilterScope)
}

interface EffectiveFilterScopeInputs<State, Props> {
  getFilterScope: (state: State, props?: Props) => unknown
  getIsActive: (state: State, props?: Props) => boolean
  getFilter: (state: State, props?: Props) => unknown
  getNormalization: (state: State, props?: Props) => unknown
}

export function createEffectiveFilterScopeSelectors<State, Props>({ getFilterScope, getIsActive, getFilter, getNormalization }: EffectiveFilterScopeInputs<State, Props>) {
  const getHasTagOrAttributeFilter = createSelector([getFilter], computeHasTagOrAttributeFilter)

  const getEffectiveFilterScope = createSelector(
    [getFilterScope, getIsActive, getHasTagOrAttributeFilter],
    (filterScope, hasBenchmark, hasTagOrAttributeFilter) => computeEffectiveFilterScope(filterScope, { hasBenchmark, hasSimulatedTrades: false, hasTagOrAttributeFilter }),
  )

  const getEffectiveNormalization = createSelector(
    [getNormalization, getEffectiveFilterScope],
    computeEffectiveNormalization,
  )

  return { getEffectiveFilterScope, getEffectiveNormalization }
}

function hasAppliedFilters(segment: unknown): boolean {
  return typeof segment === "object" && segment !== null && "filters" in segment && !isEmpty(segment.filters)
}

export function computeIsBenchmarkUnfiltered(effectiveFilterScope: FilterScope, segment: unknown): boolean {
  return effectiveFilterScope === FILTER_SCOPE.PORTFOLIO_ONLY && hasAppliedFilters(segment)
}
